
/**
 * Utility functions for score calculations across the application
 */

/**
 * Calculates the percentage score based on points earned and total possible points
 * @param pointsEarned - The actual score points earned
 * @param totalPossible - The maximum possible points
 * @returns Percentage (0-100)
 */
export const calculateScorePercentage = (pointsEarned: number, totalPossible: number): number => {
  if (totalPossible === 0) return 0;
  const percentage = Math.round((pointsEarned / totalPossible) * 100);
  // Ensure the percentage is within valid bounds
  return Math.min(100, Math.max(0, percentage));
};

/**
 * Calculates the absolute score based on percentage and total possible points
 * @param percentage - The score percentage (0-100)
 * @param totalPossible - The maximum possible points
 * @returns Absolute points
 */
export const calculateAbsoluteScore = (percentage: number, totalPossible: number): number => {
  if (percentage === null || percentage === undefined) return 0;
  return Math.round((percentage / 100) * totalPossible) || 0;
};

/**
 * Calculates the total score from individual question scores
 * @param scores - Record of question IDs to their scores
 * @param questions - Array of questions with their maximum marks
 * @returns Object with both absolute points and percentage
 */
export const calculateTotalScore = (
  scores: Record<string, number>,
  questions: { id: string; marks: number }[]
): { points: number; percentage: number; totalPossible: number } => {
  // Calculate total possible score
  const totalPossible = questions.reduce((acc, q) => acc + q.marks, 0);
  
  // If no scores or no questions, return zeros
  if (Object.keys(scores).length === 0 || questions.length === 0) {
    return { points: 0, percentage: 0, totalPossible };
  }
  
  // Calculate total earned points
  const totalEarned = questions.reduce((acc, question) => {
    // Only add score if it exists for this question ID
    const score = scores[question.id] || 0;
    return acc + score;
  }, 0);
  
  // Calculate percentage
  const percentage = calculateScorePercentage(totalEarned, totalPossible);
  
  return { points: totalEarned, percentage, totalPossible };
};

/**
 * Validates if a score is within the allowed range for a question
 * @param score - The score to validate
 * @param maxScore - The maximum allowed score
 * @returns Valid score within the range 0-maxScore
 */
export const validateScore = (score: number, maxScore: number): number => {
  return Math.min(Math.max(0, score), maxScore);
};

/**
 * Gets a color based on score percentage for UI presentation
 * @param percentage - Score percentage (0-100)
 * @returns Color class name for the appropriate score range
 */
export const getScoreColorClass = (percentage: number): string => {
  if (percentage >= 80) return "text-green-600 dark:text-green-500";
  if (percentage >= 60) return "text-amber-600 dark:text-amber-500";
  if (percentage >= 40) return "text-orange-600 dark:text-orange-500";
  return "text-red-600 dark:text-red-500";
};
